import { useRef } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import type { AttachmentUpload } from '../types';

interface AttachmentUploaderProps {
  value: AttachmentUpload[];
  onChange: (next: AttachmentUpload[]) => void;
  maxSizeMb?: number;
  maxFiles?: number;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** Reusable base64 attachment picker (backend accepts { data, name, type }). */
export function AttachmentUploader({ value, onChange, maxSizeMb = 25, maxFiles = 10 }: AttachmentUploaderProps) {
  const ref = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > maxSizeMb * 1024 * 1024) return;
    if (value.length >= maxFiles) return;
    const data = await readAsDataUrl(file);
    onChange([...value, { data, name: file.name, type: file.type }]);
  };

  return (
    <div className="space-y-2">
      {value.map((a, i) => (
        <div key={i} className="flex items-center justify-between rounded border border-outline bg-surface-variant px-3 py-2">
          <span className="flex min-w-0 items-center gap-2 text-data-md text-on-surface">
            <Paperclip className="h-4 w-4 shrink-0" />
            <span className="truncate">{a.name}</span>
          </span>
          <button type="button" aria-label="Remove" onClick={() => onChange(value.filter((_, j) => j !== i))} className="rounded p-1 hover:bg-surface">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      {value.length < maxFiles && (
        <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()}>
          <Paperclip className="h-4 w-4" /> Attach file
        </Button>
      )}
      <input ref={ref} type="file" className="hidden" onChange={handleFile} />
    </div>
  );
}
